import { useEffect, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { useRBAC } from '@/lib/rbac/context'
import { PersonPickerBar } from '@/components/performance/bonificacao/PersonPickerBar'
import { ProaBanner } from '@/components/performance/bonificacao/ProaBanner'
import { GateExplanation } from '@/components/performance/bonificacao/GateExplanation'
import { SimulatorCard } from '@/components/performance/bonificacao/SimulatorCard'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import {
  useBonusSimulate,
  useBonusDashboard,
  useBonusAssessment,
  useProaPercent,
} from '@/api/bonificacao-hooks'
import { isDirectorRole, isRHRole } from '@/lib/bonificacao/permissions'
import { Skeleton } from '@/components/ui/skeleton'
import { Card } from '@/components/ui/Card'
import { Trophy, Lock } from 'lucide-react'

interface SimuladorSearch {
  employee?: string
}

function SimuladorPage() {
  const { employee } = Route.useSearch()
  const setSelectedEmployee = useBonificacaoStore((s) => s.setSelectedEmployee)
  const selectedEmployeeId = useBonificacaoStore((s) => s.selectedEmployeeId)
  const selectedYear = useBonificacaoStore((s) => s.selectedYear)
  const currentAssessmentId = useBonificacaoStore((s) => s.currentAssessmentId)
  const { user } = useRBAC()
  const userRole = user?.role ?? ''

  const isDirector = isDirectorRole(userRole)
  const isRH = isRHRole(userRole)
  const ownEmployeeId = user?.employee?.employeeId ?? null

  // Director guard: force own employee ID
  useEffect(() => {
    if (employee) {
      setSelectedEmployee(employee)
    } else if (isDirector && ownEmployeeId) {
      setSelectedEmployee(ownEmployeeId)
    }
  }, [])

  // Data queries
  const { isLoading: dashboardLoading } = useBonusDashboard(selectedYear)

  const { data: assessment, isLoading: assessmentLoading } = useBonusAssessment(
    currentAssessmentId,
    { enabled: !!currentAssessmentId }
  )

  const { data: bonusResult, isLoading: simulateLoading } = useBonusSimulate(
    currentAssessmentId,
    { enabled: !!currentAssessmentId }
  )

  const { data: proaData, isLoading: proaLoading } = useProaPercent(selectedYear)

  const employeeName = useMemo(() => {
    return assessment?.employee?.name ?? ''
  }, [assessment])

  const salarioBase = assessment?.employee?.salary ?? 0

  const isLoading = dashboardLoading || assessmentLoading || simulateLoading

  // Director visibility: allow if finalizado OR any category is published
  const hasPublishedCategories = Object.values(assessment?.publicados ?? {}).some((v) => v)
  const directorBlocked = isDirector && assessment && assessment.status !== 'finalizado' && !hasPublishedCategories

  // Empty state: no assessment selected
  if (!currentAssessmentId && !isLoading) {
    return (
      <div className="space-y-4">
        {!isDirector && <PersonPickerBar />}
        <Card className="p-8 text-center" data-testid="simulator-empty">
          <Trophy className="mx-auto text-brand-gray mb-3" size={40} />
          <p className="text-brand-gray">
            Nenhuma avaliação de bonificação encontrada para o colaborador selecionado
          </p>
        </Card>
      </div>
    )
  }

  // Director blocked: assessment not finalized
  if (directorBlocked) {
    return (
      <div className="space-y-4">
        <Card className="p-8 text-center border border-brand-gray/10" data-testid="simulator-director-blocked">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-gray/10 mb-4">
            <Lock className="text-brand-gray/60" size={28} />
          </div>
          <p className="text-brand-gray text-lg font-display mb-1">
            Simulador bloqueado
          </p>
          <p className="text-brand-gray/60 text-sm">
            Disponível apenas após a finalização da avaliação
          </p>
        </Card>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {!isDirector && <PersonPickerBar />}
        <Skeleton variant="text" className="w-full h-24" />
        <Skeleton variant="text" className="w-full h-32" />
        <Skeleton variant="text" className="w-full h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!isDirector && <PersonPickerBar />}

      <ProaBanner
        meta={proaData?.percent != null ? 100 : 0}
        atingido={proaData?.percent ?? 0}
        passed={proaData?.passed ?? false}
        isLoading={proaLoading}
        noData={!proaLoading && !proaData}
      />

      <GateExplanation proaGateAtivo={proaData?.passed !== undefined} />

      {bonusResult && (
        <>
          <SimulatorCard
            bonusResult={bonusResult}
            salarioBase={salarioBase}
            employeeName={employeeName}
            publicados={assessment?.publicados}
            isRH={isRH}
          />
        </>
      )}
    </div>
  )
}

const SimuladorPageProtected = withPageProtection(SimuladorPage, PAGES.PERFORMANCE_BONIFICACAO)

export const Route = createFileRoute('/_auth/performance/bonificacao/simulador')({
  component: SimuladorPageProtected,
  validateSearch: (search: Record<string, unknown>): SimuladorSearch => ({
    employee: search.employee as string | undefined,
  }),
})
